import { defineMessages, useIntl } from 'react-intl-next';

import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { formatBundleDate } from '@app/utils';

import { useBundleDates } from '../../hooks';

const messages = defineMessages({
  bundleDate: {
    id: 'appUpdates.bundles.filters.bundleDate.label',
    description: 'Label for the bundle date filter',
    defaultMessage: 'Preview release date',
  },
});

export const bundleDateFilterFieldId = 'app-updates-bundle-date-filter';

export const toBundleDateOption = (date: string): SingleOption => ({
  label: formatBundleDate(date),
  value: date,
  optionType: 'option',
});

export const getBundleDateFromOptions = (
  options: readonly SingleOption[],
): string | null => {
  const latest = options.length > 0 ? options[options.length - 1] : null;
  return latest ? String(latest.value) : null;
};

export const useBundleDateFilterConfig = (): PaginatedSelectedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();

  const selectedBundleDate = state.bundles.draftFilters.selectedBundleDate;
  const selectedProducts = state.bundles.draftFilters.selectedProducts;

  const { allBundleDates: bundleDates, isLoading } =
    useBundleDates(selectedProducts);

  const getSelectedValue = (): SingleOption[] =>
    selectedBundleDate ? [toBundleDateOption(selectedBundleDate)] : [];

  // PaginatedFilter is inherently multi-select; enforce single-select by keeping
  // only the most recently chosen option.
  const onBundleDateChange = (options: readonly SingleOption[]) => {
    dispatch({
      type: 'BUNDLE_FILTER_DATE_SELECT',
      payload: { selectedBundleDate: getBundleDateFromOptions(options) },
    });
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = bundleDates.map(toBundleDateOption);

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({ options, totalCount: options.length });
  };

  return {
    testId: 'app-updates-bundle-filter-date-testid',
    fieldId: bundleDateFilterFieldId,
    fieldLabel: formatMessage(messages.bundleDate),
    onLoadOptions,
    value: getSelectedValue(),
    onChange: onBundleDateChange,
    onClearValues: () => onBundleDateChange([]),
    isLoading,
  };
};
